import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Fade, makeStyles, Typography } from '@material-ui/core';
import classNames from 'classnames';
import NotificationCard from '../../shared/notificationCard';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import DocumentTypeCard from '../../shared/documentTypeCard';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AlertDialog from '../../shared/alertDialog';
import Tooltip from '../../shared/tooltip';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-triangle-rounded.svg';
import { APP_PAGES } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { documentCategoriesSelector } from '../../../store/selectors/documentCategoriesSelector';
import {
  getPersonDocumentCategories,
  patchDocument,
  postDocument,
  deleteDocumentById,
  clearDocumentCategories,
} from '../../../store/modules/documentCategories';
import { useTranslations } from '../../../utility/useTranslations';
import { getSubdomain, showSuccessMessage } from '../../../utility/uiUtils';
import { API_DOMAIN, API_PROTOCOL } from '../../../utility/http';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';
import { isArrayEmpty, isObjectEmpty } from '../../../utility/helpers';
import {
  documentFormFields,
  getDocumentFormInitialData,
  prepareDocumentExpiryFlags,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    maxWidth: 900,
    marginTop: spacing(2.5),
  },
  description: {
    paddingBottom: spacing(4),
    ...sticky(primary.white, 170),
  },
  progressWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginBottom: spacing(1),
    width: '100%',
  },
  progressDescriptionWrapper: {
    marginBottom: spacing(4),
  },
  progressSegments: {
    display: 'flex',
    flex: 1,
    width: '100%',
    alignItems: 'center',
    borderRadius: 30,
    height: 8,
    backgroundColor: primary.bluish7,
  },
  progressSegmentFilled: {
    borderRadius: 30,
    height: 8,
    backgroundColor: primary.bluish1,
  },
  progressPercent: {
    flexShrink: 0,
    color: primary.bluish1,
    minWidth: 32,
    textAlign: 'right',
  },
  expiryFlags: {
    position: 'absolute',
    right: 0,
    bottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
  },
  summaryExpiryIcon: {
    '&&': {
      marginTop: 0,
    },
  },
  categorySection: {
    marginBottom: spacing(4),
    paddingTop: spacing(2.5),
  },
  categoryHeader: {
    marginBottom: spacing(5),
  },
  documentTypesWrapper: {
    gap: 16,
    display: 'grid',
    '&:last-of-type': {
      borderBottom: `1px solid ${primary.bluish7}`,
      paddingBottom: spacing(6.75),
    },
  },
  expiredIcon: {
    display: 'block',
    width: 16,
    height: 16,
    flexShrink: 0,
    marginTop: spacing(-0.5),
    '&&': {
      cursor: 'default',
    },
    '& path': {
      fill: primary.red1,
    },
  },
  soonToExpireIcon: {
    display: 'block',
    width: 16,
    height: 16,
    flexShrink: 0,
    marginTop: spacing(-0.5),
    '&&': {
      cursor: 'default',
    },
    '& path': {
      fill: primary.orange,
    },
  },
  documentForm: {
    display: 'grid',
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    gap: spacing(4),
  },
  switchLabel: {
    lineHeight: '20px',
    fontSize: 14,
  },
}));

const PeopleDocumentsPage = ({
  location,
  auth,
  accessControl,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;
  const scrollToDocumentId = location?.state?.notifications?.objectId;
  const translations = useTranslations(APP_PAGES.PEOPLE_DOCUMENTS);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isDocumentDrawerOpened, setIsDocumentDrawerOpened] = useState(false);
  const [isDeleteDocumentDialogOpened, setIsDeleteDocumentDialogOpened] =
    useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState({});

  const { documentCategories } = useSelector(documentCategoriesSelector);

  const canManage = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_DOCUMENTS_MANAGE,
    accessControl,
    auth.id,
    userId
  );
  const isCurrentUserProfile = auth.id === userId;

  const { totalDocuments, progress } = useMemo(() => {
    let total = 0;
    let filled = 0;

    documentCategories.forEach(category => {
      (category.types || []).forEach(type => {
        total = type.is_mandatory ? total + 1 : total;
        const isFilled =
          !isObjectEmpty(type.document || {}) && type.is_mandatory;
        if (isFilled) {
          filled += 1;
        }
      });
    });

    return { totalDocuments: total, progress: filled };
  }, [documentCategories]);

  const { hasExpired, hasSoonToExpire } = useMemo(
    () => prepareDocumentExpiryFlags(documentCategories),
    [documentCategories]
  );

  const progressWidth = progress
    ? Math.round((progress * 864) / totalDocuments)
    : progress;

  const hasCategories = !isArrayEmpty(documentCategories);
  const hasSelectedDocument =
    !isObjectEmpty(selectedDocumentType) &&
    !isObjectEmpty(selectedDocumentType?.document || {});

  const getInitialData = useCallback(async () => {
    await Promise.all([getPersonDocumentCategories(dispatch, userId)]).then(
      async () => {
        setIsInitialLoad(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const cleanup = useCallback(() => {
    dispatch(clearDocumentCategories());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleOpenDocumentDrawer = useCallback(
    documentType => () => {
      setSelectedDocumentType(documentType);
      setIsDocumentDrawerOpened(true);
    },
    []
  );

  const handleCloseDocumentDrawer = () => {
    setSelectedDocumentType({});
    setIsDocumentDrawerOpened(false);
  };

  const onSaveDocument = async values => {
    let params = {
      user: userId,
      type: selectedDocumentType.id,
      description: values.description,
      signing_date: values.signing_date,
      expire_date: values.expire_date,
      file: values.upload_file,
    };
    let savedDocument = {};

    if (hasSelectedDocument) {
      params = { ...params, documentId: selectedDocumentType.document?.id };

      savedDocument = await patchDocument(dispatch, params);
    } else {
      savedDocument = await postDocument(dispatch, params);
    }

    handleCloseDocumentDrawer();
    showSuccessMessage(
      hasSelectedDocument
        ? translations.successMessages.updatedDocument
        : translations.successMessages.savedDocument
    );

    return savedDocument;
  };

  const handleOpenDeleteDocumentDialog = () => {
    setIsDeleteDocumentDialogOpened(true);
  };

  const onDeleteDocument = async () => {
    setIsDeleteDocumentDialogOpened(false);
    handleCloseDocumentDrawer();

    await deleteDocumentById(dispatch, {
      documentId: selectedDocumentType.document?.id,
      typeId: selectedDocumentType.id,
    });

    showSuccessMessage(translations.successMessages.deletedDocument);
  };

  const subdomain = getSubdomain(window.location.hostname);

  const getFileUrl = (fileUrl = '') => {
    if (fileUrl && fileUrl.includes('http')) return fileUrl;
    if (subdomain && subdomain.length > 0) {
      return `${API_PROTOCOL}://${subdomain}.api.${API_DOMAIN}${fileUrl}`;
    }
    return null;
  };

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      location={location}
      auth={auth}
      accessControl={accessControl}
      userId={userId}
      dispatch={dispatch}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <Typography
            className={classes.description}
            variant="body1"
            component="div"
          >
            {translations.pageDescription}
            <div className={classes.expiryFlags}>
              <Fade in={hasExpired} unmountOnExit>
                <div>
                  <Tooltip
                    text={translations.expiredDocumentsTooltip}
                    icon={WarningIcon}
                    customIconClass={classNames(
                      classes.expiredIcon,
                      classes.summaryExpiryIcon
                    )}
                  />
                </div>
              </Fade>
              <Fade in={hasSoonToExpire} unmountOnExit>
                <div>
                  <Tooltip
                    text={translations.soonToExpireDocumentsTooltip}
                    icon={WarningIcon}
                    customIconClass={classNames(
                      classes.soonToExpireIcon,
                      classes.summaryExpiryIcon
                    )}
                  />
                </div>
              </Fade>
            </div>
          </Typography>
          <div className={classes.root}>
            <div className={classes.progressDescriptionWrapper}>
              <div className={classes.progressWrapper}>
                <div className={classes.progressSegments}>
                  <div
                    className={classes.progressSegmentFilled}
                    style={{ width: progressWidth }}
                  />
                </div>
                <Typography
                  variant="overline"
                  className={classes.progressPercent}
                >
                  {`${progress} / ${totalDocuments}`}
                </Typography>
              </div>
              <Typography variant="overline">
                {translations.progressDescription}
              </Typography>
            </div>
            {hasCategories &&
              documentCategories.map(category => {
                return (
                  <div
                    key={`category_${category.name}`}
                    className={classes.categorySection}
                  >
                    <div className={classes.categoryHeader}>
                      <Typography variant="h3">{category.name}</Typography>
                    </div>
                    <div className={classes.documentTypesWrapper}>
                      {category.types.map(documentType => (
                        <DocumentTypeCard
                          key={`document_type_${documentType.name}`}
                          translations={translations}
                          documentType={documentType}
                          expireWarningDays={category.expire_warning_days}
                          canManage={canManage}
                          isCurrentUserProfile={isCurrentUserProfile}
                          shouldScrollIntoView={
                            scrollToDocumentId != null &&
                            documentType.document?.id === scrollToDocumentId
                          }
                          getFileUrl={getFileUrl}
                          onOpenDocumentDrawer={handleOpenDocumentDrawer}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
          <NotificationCard
            title={translations.noResults.title}
            content={translations.noResults.content}
            shouldFade={!isInitialLoad && !hasCategories}
          />
          <CustomFormDrawer
            customContentClass={classes.documentForm}
            translations={translations.documentForm}
            isOpened={isDocumentDrawerOpened}
            initialData={getDocumentFormInitialData(selectedDocumentType)}
            fields={documentFormFields(classes)}
            onClose={handleCloseDocumentDrawer}
            onSave={onSaveDocument}
            onDelete={handleOpenDeleteDocumentDialog}
            isInitialValid={hasSelectedDocument}
            hasCancelButton
          />
          <AlertDialog
            isOpened={isDeleteDocumentDialogOpened}
            translations={translations.deleteDocumentDialog}
            onClose={() => setIsDeleteDocumentDialogOpened(false)}
            onConfirm={onDeleteDocument}
            isWarning
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleDocumentsPage.propTypes = {
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(PeopleDocumentsPage);
